import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { settingsApi } from '../api';
import type { AppSettings } from '../types';

interface SettingsContextValue {
  settings: AppSettings | null;
  reload: () => void;
  /** Formate une date selon le fuseau horaire configuré */
  formatDate: (date: string | Date | undefined, opts?: Intl.DateTimeFormatOptions) => string;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: null,
  reload: () => {},
  formatDate: (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—'),
});

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings | null>(null);

  const load = useCallback(() => {
    settingsApi.get()
      .then(r => setSettings(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatDate = useCallback(
    (date: string | Date | undefined, opts?: Intl.DateTimeFormatOptions) => {
      if (!date) return '—';
      const tz = settings?.timezone ?? 'Europe/Luxembourg';
      const lang = settings?.default_language ?? 'fr';
      const locale = lang === 'de' ? 'de-DE' : lang === 'en' ? 'en-GB' : 'fr-FR';
      try {
        return new Intl.DateTimeFormat(locale, {
          timeZone: tz,
          day: '2-digit', month: '2-digit', year: 'numeric',
          ...opts,
        }).format(new Date(date));
      } catch {
        return new Date(date).toLocaleDateString('fr-FR');
      }
    },
    [settings],
  );

  return (
    <SettingsContext.Provider value={{ settings, reload: load, formatDate }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
